import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { ApplicableRulesCard } from '@atlas/ui-domain'
import { Badge, Button, Chip } from '@atlas/ui-primitives'
import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { MESSAGE_STATUS_LABEL, MESSAGE_TYPE_LABEL } from '@/domain/message.types'
import { useDeleteMessage, useMessage } from '@/hooks/useMessages'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { type AttributeChip, useMessageAttributes, useMessageClassification } from './classification'
import { type MessageOrderLink, MessageOrderLinks } from './MessageOrderLinks'
import { TranslationsSection } from './TranslationsSection'
import { useMessageRules } from './useMessageRules'
import { useTranslationsPanel } from './useTranslationsPanel'

/**
 * The message's `order_code` values, resolved to the orders they name. The attribute
 * stores the CODE, and the detail route takes the id, so the board list is what turns
 * one into the other; a code it does not cover keeps its link with no destination.
 */
function useMessageOrderLinks(attributes: AttributeChip[]): MessageOrderLink[] {
	const { data: workOrders = [] } = useWorkOrders()
	const byCode = new Map(workOrders.map((o) => [o.orderCode, o]))
	return attributes.map(({ value }) => {
		const order = byCode.get(value)
		return { orderCode: value, workOrderId: order?.workOrderId ?? null, title: order?.title ?? null }
	})
}

/** Message details (route `/messages/:id`). */
export function MessageDetailPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: message, isLoading } = useMessage(id ?? null)
	const { data: dimensions = [] } = useMessageClassification(id ?? null)
	const { data: attributes = [] } = useMessageAttributes(id ?? null)
	const orderLinks = useMessageOrderLinks(attributes)
	const remove = useDeleteMessage()
	const translations = useTranslationsPanel(message)
	const rules = useMessageRules(id ?? null, dimensions)

	if (isLoading) return <ListSkeleton label="Loading…" />
	if (!message) return <p className="text-ds-text-muted">Message not found.</p>

	const hasClassification = dimensions.length > 0

	return (
		<div>
			<Link
				to="/messages"
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Messages
			</Link>
			<PageHeader
				eyebrow="Messages"
				title={message.title}
				actions={
					<div className="flex gap-2">
						<Button
							variant="secondary"
							className="rounded-ds-full"
							onPress={() => void navigate(`/messages/${id}/edit`)}
						>
							Edit
						</Button>
						<Button
							variant="secondary"
							className="rounded-ds-full"
							isDisabled={remove.isPending}
							onPress={() => remove.mutate(message.messageId, { onSuccess: () => void navigate('/messages') })}
						>
							Delete
						</Button>
					</div>
				}
			/>

			<div className="mt-6 flex flex-wrap items-center gap-2">
				<Badge>{MESSAGE_TYPE_LABEL[message.messageType]}</Badge>
				<Badge variant="secondary">{message.languageIso}</Badge>
				<Badge variant="muted">{MESSAGE_STATUS_LABEL[message.status]}</Badge>
				{message.isSource ? <Badge variant="success">Source</Badge> : <Badge variant="muted">Translation</Badge>}
			</div>

			{message.description ? <p className="mt-4 text-sm text-ds-text-muted">{message.description}</p> : null}

			<SectionCard title="Content" className="mt-6 p-6">
				<p className="whitespace-pre-wrap text-ds-text">{message.content}</p>
			</SectionCard>

			{hasClassification ? (
				<SectionCard title="Classification" className="mt-4 p-6">
					<div className="flex flex-wrap gap-1.5">
						{dimensions.map((t) => (
							<Chip key={`${t.axisCode}:${t.valueCode}`} variant="secondary" size="sm">
								{t.label}
							</Chip>
						))}
					</div>
				</SectionCard>
			) : null}

			<MessageOrderLinks orders={orderLinks} />

			<div className="mt-4">
				<ApplicableRulesCard
					rules={rules.rules}
					isLoading={rules.isLoading}
					canEdit={rules.canEdit}
					onEditRule={rules.editRule}
					ruleHref={rules.ruleHref}
					onOpenRule={rules.openRule}
					title="Rules governing this message's text"
					note={
						rules.canEdit
							? 'These rules go into the generated translations below. Editing one changes the rule everywhere it resolves — not only on this message.'
							: 'These rules go into the generated translations below.'
					}
				/>
			</div>

			<TranslationsSection
				variants={translations.variants}
				currentMessageId={message.messageId}
				availableLocaleOptions={translations.availableLocaleOptions}
				onAddLocale={translations.addLocale}
				isAddingLocale={translations.isAddingLocale}
				addLocaleError={translations.addLocaleError}
				onTranslate={translations.translate}
				requestingLocales={translations.requestingLocales}
				translateErrors={translations.translateErrors}
			/>
		</div>
	)
}
